import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Account, ConfigService, GroupOrder, Order, StoreConfig, UpsellGroup, Utils, CheckoutUpsellArea } from 'ngx-web-api';
import { Subscription, Observable, of, noop } from 'rxjs';
import { mergeMap, finalize, take, map, tap, filter } from 'rxjs/operators';
import { GroupOrderOrchestratorService } from '../../core/services/group-order-orchestrator.service';
import { ModalService } from '../../core/services/modal.service';
import { OrderUpsellService } from '../../core/services/order-upsell.service';
import { ThemeService } from '../../core/services/theme.service';
import { CheckoutUpsellsModalContext } from '../../domain/checkout-upsells-modal-context';
import { Theme } from '../../domain/theme.model';
import { UpsellItemInfo } from '../../domain/upsell-item-info';
import { CheckoutUpsellsModalComponent } from '../checkout-upsells-modal/checkout-upsells-modal.component';
import { UiOrchestratorService } from '../../core/services/ui-orchestrator.service';
import { TranslateService } from '@ngx-translate/core';
import { EcommerceTriggeringArea } from '../../domain/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../../domain/ecommerce-triggering-point.enum';
import { OrderingUtilsService } from 'app/core/services/ordering-utils.service';

@Component({
  selector: 'fts-checkout-button',
  templateUrl: './checkout-button.component.html',
  styleUrls: ['./checkout-button.component.scss'],
})
export class CheckoutButtonComponent implements OnInit, OnDestroy {
  @Input()
  groupOrder: GroupOrder;
  @Input()
  order: Order;
  @Input()
  account: Account;
  @Input()
  inverted: boolean;
  @Input()
  isUpsell?: boolean;
  @Input()
  reduceBtnSize?: boolean;
  @Input()
  buttonId?: string;
  @Input()
  isInItemGroupCreationMode?: boolean;
  @Output()
  checkoutHandled: EventEmitter<void> = new EventEmitter<void>();

  public mergingGroupOrder: Subscription;
  private theme: Theme;
  private storeConfig: StoreConfig;
  public disableCheckout = true;
  public placeOrderSubscription: Subscription;
  public loadingCheckoutPageSubsciption: Subscription;
  public errorMessage: string;
  isSelfOrder: boolean;
  subscriptions: Subscription[];

  constructor(
    private router: Router,
    private modalService: ModalService,
    private groupOrderService: GroupOrderOrchestratorService,
    private themeService: ThemeService,
    private configService: ConfigService,
    private orderUpsellService: OrderUpsellService,
    private uiOrchestratorService: UiOrchestratorService,
    private translateService: TranslateService,
    private orderingUtilsService: OrderingUtilsService
  ) {}

  ngOnInit(): void {
    this.subscriptions = [
      this.themeService.theme.subscribe(theme => (this.theme = theme)),
      this.configService.storeConfig$.subscribe(config => (this.storeConfig = config)),
      this.uiOrchestratorService.checkoutButtonDisabled$.subscribe(disabled => (this.disableCheckout = disabled)),
      this.orderingUtilsService.isSelfOrder$.subscribe(isSelfOrder => (this.isSelfOrder = isSelfOrder)),
    ];
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  handleCheckoutClick() {
    if (this.isDisabled()) {
      if (!!this.errorMessage) {
        this.modalService.openWarningNotificationModal(this.errorMessage);
      }
      return;
    }
    if (
      this.account &&
      this.account.isInstantiated &&
      !(this.storeConfig.allowUnverifiedUsersToOrder || (this.account.verifiedEmail && !this.account.flagBouncedEmail)) &&
      !this.isSelfOrder
    ) {
      this.modalService.promptUserToVerifyEmail(this.account.email);
      return;
    }

    this.loadingCheckoutPageSubsciption = this.router.events
      .pipe(
        filter(e => {
          return e instanceof NavigationEnd && e.urlAfterRedirects === '/checkout/details';
        }),
        take(1)
      )
      .subscribe(noop);

    this.proceedToCheckout();
    this.checkoutHandled.emit();
  }

  private openUpsellsWizard(upsellGroups: UpsellGroup[], proceedToCheckout = true): Observable<any> {
    const upsellItemInfos: UpsellItemInfo[] = Utils.flatten(
      upsellGroups.map(group =>
        group.upsellItems.map(upsellItem => ({
          upsellItem,
          title: group.title,
        }))
      )
    );
    const context = new CheckoutUpsellsModalContext(upsellItemInfos, proceedToCheckout).getConfig();
    return this.modalService.openModal(CheckoutUpsellsModalComponent, context);
  }

  private shouldHandleAsGroupOrderCheckout(): boolean {
    return this.groupOrder && this.groupOrder.isActive && !this.groupOrder.merged;
  }

  private shouldShowOrderWarningsModal(): boolean {
    return this.theme.hasTopOrderTree && this.order.hasWarnings;
  }

  private proceedToCheckout() {
    const navigateToCheckoutDetails$ = of(null).pipe(
      tap(() => {
        this.router.navigate(['/checkout', 'details'], {
          state: {
            ecommerceTriggeringArea: EcommerceTriggeringArea.ORDER_TREE,
            ecommerceTriggeringPoint: EcommerceTriggeringPoint.CHECKOUT_BUTTON,
          },
        });
      })
    );

    if (this.shouldHandleAsGroupOrderCheckout()) {
      this.handleAsGroupOrderCheckout();
      return;
    }
    if (this.shouldShowOrderWarningsModal()) {
      this.modalService.openOrderWarningsModal(this.order);
      return;
    }

    if (this.storeConfig.checkoutUpsellArea === CheckoutUpsellArea.MENU) {
      this.uiOrchestratorService.disableCheckoutButton(true);
      this.placeOrderSubscription = this.orderUpsellService
        .fetchUpsellGroups()
        .pipe(
          mergeMap((upsellGroups: UpsellGroup[]) => {
            if (upsellGroups.length) {
              return this.openUpsellsWizard(upsellGroups, false).pipe(map(() => true));
            }
            return of(true);
          }),
          mergeMap(() => navigateToCheckoutDetails$),
          take(1),
          finalize(() => this.uiOrchestratorService.disableCheckoutButton(false))
        )
        .subscribe(
          () => {
            this.checkoutHandled.emit();
          },
          err => {
            this.modalService.parseAndNotifyErrors(err);
          }
        );
    } else {
      this.placeOrderSubscription = navigateToCheckoutDetails$.subscribe(noop, noop);
    }
  }

  get isVisible(): boolean {
    if (!this.groupOrder || !this.groupOrder.isActive) {
      return true;
    } else if (this.groupOrder && this.groupOrder.isActive) {
      const member = this.groupOrder.me;
      return this.groupOrder.asLeader && (this.groupOrder.merged || (!!member && member.ready));
    } else {
      return false;
    }
  }

  private isDisabled(): boolean {
    if (this.disableCheckout) {
      return true;
    }
    if (!this.groupOrder || !this.groupOrder.isActive) {
      if (!this.theme.hasTopOrderTree && this.order.hasWarnings) {
        this.modalService.openOrderWarningsModal(this.order);
        this.errorMessage = null;
        return true;
      }
      if (this.order.itemsCount <= 0) {
        this.errorMessage = this.translateService.instant('component.checkout_button.no_items');
        return true;
      }
      if (this.isInItemGroupCreationMode) {
        this.errorMessage = this.translateService.instant('component.checkout_button.creating_group');
        return true;
      }
    }
    if (!this.groupOrderService.canReadyLeaderCheckout(this.groupOrder, this.order)) {
      this.errorMessage =
        this.order.itemsCount === 0
          ? this.translateService.instant('component.checkout_button.no_items')
          : this.translateService.instant('component.checkout_button.leader_ready');
      return true;
    }
    return false;
  }

  private handleAsGroupOrderCheckout() {
    this.mergingGroupOrder = this.groupOrderService.continueToCheckout(() => (this.loadingCheckoutPageSubsciption = null));
  }
}
